import { useCallback, useState } from 'react';
import { Field, Form } from 'react-final-form';

import { useSignupModalStyles as usePartnerSignupStyles } from '@/auth/components/shared/useSignupModalStyles';
import Button from '@/components/Button';
import { TextField } from '@/components/FormAdapters';

import { STEP_COPY } from '../constants';
import type { useSelfServiceSMBSignupFlow } from '../hooks/useSelfServiceSMBSignupFlow';
import type { DomainMatch } from '../types';
import { mapTeamJoinError } from '../utils/teamJoinError';
import { useStyles as useTeamStepStyles } from './TeamStep.styles';

interface TeamStepProps {
  flow: ReturnType<typeof useSelfServiceSMBSignupFlow>;
  // OTP token is optional — see TeamJoinStep for the dual-auth rationale.
  onRequestToJoin: (input: {
    groupId: string;
    otpVerificationToken?: string;
  }) => Promise<{ autoApproved: boolean }>;
}

interface FormValues {
  company_name?: string;
}

function validate(values: FormValues): Partial<Record<keyof FormValues, string>> {
  const errors: Partial<Record<keyof FormValues, string>> = {};
  if (!values.company_name?.trim()) errors.company_name = 'Enter your company name';
  return errors;
}

export function TeamStep({ flow, onRequestToJoin }: TeamStepProps): JSX.Element {
  const s = usePartnerSignupStyles();
  const styles = useTeamStepStyles();
  const { state } = flow;
  const [busy, setBusy] = useState(false);

  const matchedTeam: DomainMatch | null = state.selectedMatch ?? state.domainMatches[0] ?? null;
  const hasMatch = Boolean(matchedTeam) && state.domainMatches.length > 0;

  const submitNoMatch = useCallback(
    (values: FormValues) => {
      flow.submitTeamCreate(values.company_name!.trim());
    },
    [flow]
  );

  // On success, transition the flow to 'team-request-sent' so the
  // consolidated TeamRequestSentStep renders the awaiting-approval UI in
  // one place (mirrors the resume path from viewer.pendingTeamMembership).
  const requestJoin = useCallback(async () => {
    if (!matchedTeam) return;
    try {
      setBusy(true);
      const { autoApproved } = await onRequestToJoin({
        groupId: matchedTeam.id,
        ...(state.emailOtpToken ? { otpVerificationToken: state.emailOtpToken } : {}),
      });
      flow.joinRequested(matchedTeam, autoApproved);
    } catch (err) {
      const { code, message } = mapTeamJoinError(err);
      flow.setError(code, message);
    } finally {
      setBusy(false);
    }
  }, [flow, matchedTeam, onRequestToJoin, state.emailOtpToken]);

  if (state.domainMatchLoading) {
    return (
      <div className={s.formContent}>
        <p role="status" className={styles.loadingStatus}>
          Checking your email…
        </p>
      </div>
    );
  }

  if (hasMatch && matchedTeam) {
    return (
      <>
        <div className={`${s.formContent} ${styles.content}`}>
          <header>
            <h2 id="self-service-smb-team-title" className={styles.title}>
              {STEP_COPY.team.joinTitle(matchedTeam.name)}
            </h2>
            <p className={styles.subtitle}>{STEP_COPY.team.joinSubtitle}</p>
          </header>
          {state.error && (
            <p role="alert" className={styles.errorText}>
              {state.error.message}
            </p>
          )}
          <div data-testid="domain-match-banner" className={styles.domainMatchBanner}>
            We found a match for your email domain — <strong>{matchedTeam.name}</strong>.
          </div>
        </div>
        <div className={s.footer}>
          <Button
            variant="text"
            size="normal"
            onClick={() => flow.restartTeamFromJoin()}
            disabled={busy}
            classes={{ root: s.backButton }}
          >
            Create a new team instead
          </Button>
          <Button
            color="teal"
            size="normal"
            onClick={requestJoin}
            disabled={busy || state.domainMatchLoading}
            classes={{ root: s.continueButton }}
          >
            {busy ? 'Sending request…' : `Join ${matchedTeam.name}`}
          </Button>
        </div>
      </>
    );
  }

  return (
    <Form<FormValues>
      onSubmit={submitNoMatch}
      validate={validate}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit} noValidate>
          <div className={`${s.formContent} ${styles.content}`}>
            <header>
              <h2 id="self-service-smb-team-title" className={styles.title}>
                {STEP_COPY.team.createTitle}
              </h2>
              <p className={styles.subtitle}>{STEP_COPY.team.createSubtitle}</p>
            </header>
            {state.error && (
              <p role="alert" className={styles.errorText}>
                {state.error.message}
              </p>
            )}
            <Field
              name="company_name"
              component={TextField}
              label="Company Name"
              variant="outlined"
              fullWidth
              required
              margin="none"
              changeOnBlur={false}
              inputProps={{ maxLength: 255 }}
            />
            <p className={styles.workspaceOwnerCallout}>{STEP_COPY.team.workspaceOwnerCallout}</p>
          </div>
          <div className={s.footer}>
            <span />
            <Button
              type="submit"
              color="teal"
              size="normal"
              disabled={busy || state.domainMatchLoading}
              classes={{ root: s.continueButton }}
            >
              {busy ? 'Creating your workspace…' : STEP_COPY.team.submit}
            </Button>
          </div>
        </form>
      )}
    />
  );
}
